// https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/CMP%20JS%20API%20v1.1%20Final.md
// https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/TCFv2/IAB%20Tech%20Lab%20-%20CMP%20API%20v2.md
// https://developers.didomi.io/cmp/web-sdk/third-parties/custom-integrations/no-tag-manager
// http://prebid.org/dev-docs/modules/consentManagement.html
import { getMqState } from 'common/module/advertisement/utils';
import loadApstagSdk from 'common/third_party/headerbiddingApstag';
import loadPrebidSdk from 'common/third_party/headerbiddingPrebid';
import { isProd } from 'common/tools/env';

let HBAlreadyRun = false;

function getBidderMqInfo(bidder, infoKeyword) {
  let info = null;
  const isMobileMq = getMqState() === 'mobile';

  if (bidder.default) {
    return bidder.default[infoKeyword];
  }

  if (isMobileMq && bidder.mobile) {
    info = bidder.mobile[infoKeyword];
  } else if (!isMobileMq && bidder.desktop) {
    info = bidder.desktop[infoKeyword];
  }

  return info;
}

function getPrebidMqSize(prebidSlotSize) {
  switch (getMqState()) {
    case 'tablet':
      return prebidSlotSize.tablet
        ? prebidSlotSize.tablet
        : prebidSlotSize.mobile;
    case 'desktop':
      return prebidSlotSize.desktop;
    default:
      return prebidSlotSize.mobile;
  }
}

function getHeaderBiddingConf(GPT_CONFIG_PAGE) {
  const bidCache = GPT_CONFIG_PAGE['headerbid-bid-cache'] || false;
  const bidTimeout = GPT_CONFIG_PAGE['headerbid-timeout'] || 1000;
  const bidGranularity = GPT_CONFIG_PAGE['headerbid-granularity'] || 'high';
  const usePrebidSize = GPT_CONFIG_PAGE.prebidUseSize || null;
  const allowAuctionWithoutConsent =
    GPT_CONFIG_PAGE.prebidAllowAuctionWithoutConsent;

  return {
    PREBID_BID_CACHE: bidCache,
    PREBID_USE_SIZE: usePrebidSize,
    PREBID_ALLOW_AUCTION_WO_CONSENT: allowAuctionWithoutConsent,
    BID_TIMEOUT: bidTimeout,
    GRANULARITY: bidGranularity,
    FAILSAFE_TIMEOUT: 3000
  };
}

function setRpVisitor(GPT_CONFIG_PAGE) {
  let rpVisitor = {};
  const persona = JSON.parse(
    window.localStorage.getItem(`nvgpersona${GPT_CONFIG_PAGE.navEgg}`)
  );

  if (persona) {
    let col;
    for (col in persona) {
      if (col) {
        rpVisitor[`nvg_${col}`] = persona[col];
      }
    }
  }

  if (window._ttprofiles) {
    const profile = window._ttprofiles;
    rpVisitor['tge'] = profile.getEquipment;
    rpVisitor['tgp'] = profile.getProfiles;
    rpVisitor['tgu'] = profile.getMicrosegments;
    rpVisitor['tgc'] = profile.getSocialClass;
    rpVisitor['tga'] = profile.getAge;
    rpVisitor['tgg'] = profile.getGender;
    rpVisitor['tgs'] = profile.getSubjects;
    rpVisitor['tgca'] = profile.getCustomAudience;
  }

  return Object.keys(rpVisitor) ? rpVisitor : null;
}

// PREBID
function setPrebidAdUnits(GPT_CONFIG_PAGE, GPT_LIST_ID) {
  const adUnits = [];

  GPT_LIST_ID.forEach(item => {
    const ad = GPT_CONFIG_PAGE[item];

    if (!ad || !ad.headerBidding || !ad.headerBidding.prebid) {
      return false;
    }

    const adPrebid = ad.headerBidding.prebid;

    const adUnit = {
      code: item,
      mediaTypes: {
        banner: {
          sizes: getPrebidMqSize(adPrebid.banner.sizes)
        },
        ...(adPrebid.video ? { video: adPrebid.video } : {})
      },
      bids: [],
      ...(adPrebid.targeting
        ? { targeting: window.GPT_TARGETING_OBJ || GPT_CONFIG_PAGE.targeting }
        : {})
    };

    adPrebid.bidders.forEach(bidder => {
      const bid = {
        bidder: bidder.name,
        params: bidder.params ? bidder.params : {}
      };

      if (bidder.inventory) {
        bid.inventory = {
          adunit:
            GPT_CONFIG_PAGE['tag-id'] + GPT_CONFIG_PAGE['tag-other-levels'],
          ...ad.targeting,
          ...GPT_CONFIG_PAGE.targeting
        };
      }

      if (bidder.ext) {
        bid.ext = {
          adunit:
            GPT_CONFIG_PAGE['tag-id'] + GPT_CONFIG_PAGE['tag-other-levels'],
          ...ad.targeting,
          ...GPT_CONFIG_PAGE.targeting
        };
      }

      if (bidder.rpVisitor) {
        bid.visitor = setRpVisitor(GPT_CONFIG_PAGE);
      }

      switch (bidder.name) {
        case 'rubicon':
          bid.params.siteId = getBidderMqInfo(bidder, 'siteId');
          bid.params.zoneId = getBidderMqInfo(bidder, 'zoneId');
          bid.params.accountId = bidder.accountId;
          break;
        case 'openx':
          bid.params.delDomain = bidder.delDomain;
          bid.params.unit = getBidderMqInfo(bidder, 'unit');
          break;
        case 'smartadserver':
          bid.params.domain = bidder.domain;
          bid.params.siteId = bidder.siteId;
          bid.params.pageId = bidder.pageId;
          bid.params.formatId = getBidderMqInfo(bidder, 'formatId');
          break;
        case 'teads':
          bid.params.placementId =
            bidder.placementId || getBidderMqInfo(bidder, 'placementId');
          bid.params.pageId = getBidderMqInfo(bidder, 'pageId');
          break;
        case 'ix':
          bid.params.siteId = getBidderMqInfo(bidder, 'siteId');
          bid.params.size = getBidderMqInfo(bidder, 'size');
          break;
        case 'criteo':
          bid.params.networkId = getBidderMqInfo(bidder, 'networkId');
          bid.params.publisherSubId = getBidderMqInfo(bidder, 'publisherSubId');
          break;
        case 'triplelift':
          bid.params.inventoryCode = getBidderMqInfo(bidder, 'inventoryCode');
          break;
        case 'onemobile':
          bid.params.dcn = getBidderMqInfo(bidder, 'dcn');
          bid.params.pos = getBidderMqInfo(bidder, 'pos');
          break;
        case 'seedtag':
          bid.params.publisherId = getBidderMqInfo(bidder, 'publisherId');
          bid.params.placement = getBidderMqInfo(bidder, 'placement');
          bid.params.adUnitId = getBidderMqInfo(bidder, 'adUnitId');
          break;
        case 'richaudience':
          bid.params.pid = getBidderMqInfo(bidder, 'pid');
          bid.params.supplyType = bidder.supplyType;
          break;
        case 'pubmatic':
          bid.params.publisherId = bidder.publisherId;
          bid.params.adSlot = getBidderMqInfo(bidder, 'adSlot');
          break;
        default:
          bid.params.placementId = getBidderMqInfo(bidder, 'placementId');
      }

      adUnit.bids.push(bid);
    });

    adUnits.push(adUnit);
  });

  return adUnits;
}

async function handleLoadAfterConsent(GPT_CONFIG_PAGE) {
  return new Promise(resolve => {
    if (!window.__tcfapi) {
      loadHbSdks(GPT_CONFIG_PAGE).then(() => {
        resolve();
      });
      return;
    }

    // TCFV2 HANDLE HB Load Scripts (with the issue for apstag tha should be loaded once consent is provided)
    // Apstag vendor status is 793
    const getConsent = tcData => {
      if (
        tcData.eventStatus === 'useractioncomplete' ||
        tcData.eventStatus === 'tcloaded'
      ) {
        window.__tcfapi('removeEventListener', 2, getConsent);
        loadHbSdks(GPT_CONFIG_PAGE).then(() => {
          resolve();
        });
      }
    };
    window.__tcfapi('addEventListener', 2, getConsent);
  });
}

async function loadHbSdks(GPT_CONFIG_PAGE) {
  return new Promise(resolve => {
    let promise1, promise2;

    loadPrebidSdk(GPT_CONFIG_PAGE.prebidUrl, () => {
      promise1 = Promise.resolve();
    });

    loadApstagSdk(() => {
      promise2 = Promise.resolve();
    });

    Promise.all([promise1, promise2]).then(() => {
      resolve();
    });
  });
}

function setPrebidConfig(headerBiddingConf) {
  const debug = /#pbjs_debug=true/.test(window.location.hash);

  const baseConfig = {
    useBidCache: headerBiddingConf.PREBID_BID_CACHE,
    priceGranularity: headerBiddingConf.GRANULARITY,
    enableSendAllBids: true,
    debug: debug,
    ...(headerBiddingConf.PREBID_USE_SIZE
      ? { improvedigital: { usePrebidSizes: true } }
      : {})
  };

  const consentConfig = {
    consentManagement: {
      cmpApi: 'iab',
      timeout: 500,
      allowAuctionWithoutConsent:
        headerBiddingConf.PREBID_ALLOW_AUCTION_WO_CONSENT
    }
  };

  const pbjsConfig = { ...baseConfig, ...consentConfig };

  if (debug) {
    // eslint-disable-next-line no-console
    console.log('prebid config : ', pbjsConfig);
  }

  return pbjsConfig;
}

export async function callStandAlonePrebid(GPT_CONFIG_PAGE, GPT_LIST_ID) {
  loadPrebidSdk(GPT_CONFIG_PAGE.prebidUrl, () => {
    const headerBiddingConf = getHeaderBiddingConf(GPT_CONFIG_PAGE);

    const pbjsConfig = setPrebidConfig(headerBiddingConf);

    window.pbjs.que.push(() => {
      window.pbjs.setConfig(pbjsConfig);
      window.pbjs.addAdUnits(setPrebidAdUnits(GPT_CONFIG_PAGE, GPT_LIST_ID));
      window.pbjs.requestBids({
        bidsBackHandler: initAdserverWithPrebid,
        timeout: headerBiddingConf.BID_TIMEOUT
      });
    });

    setTimeout(() => {
      initAdserverWithPrebid();
    }, headerBiddingConf.FAILSAFE_TIMEOUT);
  });
}

function initAdserverWithPrebid() {
  if (window.pbjs.initAdserverSet) return;
  window.pbjs.initAdserverSet = true;
  window.googletag.cmd.push(() => {
    window.pbjs.que.push(() => {
      window.pbjs.setTargetingForGPTAsync();
      window.googletag.pubads().refresh();
    });
  });
}

// AMZ APSTAG
function setApstagSlots(GPT_CONFIG_PAGE, GPT_LIST_ID) {
  const apstagSlots = [];

  GPT_LIST_ID.forEach(item => {
    const ad = GPT_CONFIG_PAGE[item];
    if (!ad || !ad.headerBidding || !ad.headerBidding.apstag) {
      return false;
    }

    const adApstagSlot = ad.headerBidding.apstag;

    const apstagSlot = {
      slotID: item,
      sizes: adApstagSlot.sizes,
      slotName: adApstagSlot.name
    };

    apstagSlots.push(apstagSlot);
  });

  return apstagSlots;
}

/**
 * AMZ APSTAG & PREBID => https://ams.amazon.com/webpublisher/uam/docs/get-started/integrating-alongside-prebid.html
 * @param {Object} GPT_CONFIG_PAGE => the global configuration for all position ads
 * @param {Array} GPT_LIST_ID => the array of ad position id (in the DOM). Either all or the ones to refresh
 * @param {Array} slotsToRefresh => the array of DFP slots needed for the refresh. If empty we refresh all position (mostly when start ads)
 * @param {function} callback
 * @returns {undefined}
 */
export async function callHeaderBidding(
  GPT_CONFIG_PAGE,
  GPT_LIST_ID,
  slotsToRefresh = [],
  callback = () => {}
) {
  try {
    await handleLoadAfterConsent(GPT_CONFIG_PAGE);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(
      "Prebid or Asptag wasn't load (error), header bidding isn't engaged. We trigger the refresh to load ads anyway"
    );
    window.googletag.cmd.push(() => {
      if (!slotsToRefresh.length) {
        window.googletag.pubads().refresh();
      } else {
        window.googletag.pubads().refresh(slotsToRefresh);
      }
    });
    return false;
  }

  if (HBAlreadyRun) {
    if (!isProd()) {
      // eslint-disable-next-line no-console
      console.warn('header bidding  already running');
    }
    return false;
  }

  HBAlreadyRun = true;

  if (slotsToRefresh.length) {
    window.pbjs.removeAdUnit();
  }

  const bidders = ['apstag', 'prebid'];
  const headerBiddingConf = getHeaderBiddingConf(GPT_CONFIG_PAGE);

  // create a requestManager to keep track of bidder state to determine when to send ad server
  // request and what apstagSlots to request from the ad server
  const requestManager = {
    adserverRequestSent: false
  };

  // loop through bidder array and add the bidders to the request manager
  bidders.forEach(bidder => {
    requestManager[bidder] = false;
  });

  // init AMZ conf.
  if (window.apstag && GPT_CONFIG_PAGE.apstag && !slotsToRefresh.length) {
    window.apstag.init({
      pubID: GPT_CONFIG_PAGE.apstag,
      adServer: 'googletag',
      bidTimeout: headerBiddingConf.BID_TIMEOUT
    });
  }

  // return true if all bidders have returned
  function allBiddersBack() {
    return (
      bidders
        .map(bidder => {
          // get the booleans from the object
          return requestManager[bidder];
          // get rid of false values - indicates that the bidder has responded
        })
        .filter(Boolean).length === bidders.length
    );
  }

  // handler for header bidder responses
  function headerBidderBack(bidder) {
    // return early if request to adserver is already sent
    if (requestManager.adserverRequestSent === true) {
      return;
    }

    // flip bidder back flag
    if (bidder === 'apstag') {
      requestManager.apstag = true;
    } else if (bidder === 'prebid') {
      requestManager.prebid = true;
    }

    // if all bidders are back, send the request to the ad server
    if (allBiddersBack()) {
      sendAdserverRequest();
    }
  }

  // actually get ads from GAM
  function sendAdserverRequest() {
    // return early if request already sent
    if (requestManager.adserverRequestSent === true) {
      return;
    }

    // flip the boolean that keeps track of whether the adserver request was sent
    requestManager.adserverRequestSent = true;

    // flip pbjs boolean to signal to pbjs the ad server has already been called
    window.pbjs.adserverRequestSent = true;

    // flip boolean for adserver request to avoid duplicate requests
    requestManager.sendAdserverRequest = true;

    // set bid targeting and make ad request to GAM
    window.googletag.cmd.push(() => {
      window.apstag.setDisplayBids();
      window.pbjs.setTargetingForGPTAsync?.();
      if (!slotsToRefresh.length) {
        window.googletag.pubads().refresh();
      } else {
        window.googletag.pubads().refresh(slotsToRefresh);
      }
    });

    // execute callback if any (else noop function)
    callback();

    HBAlreadyRun = false;
  }

  async function requestBids() {
    const adUnits = setPrebidAdUnits(GPT_CONFIG_PAGE, GPT_LIST_ID);
    const apstagSlots = setApstagSlots(GPT_CONFIG_PAGE, GPT_LIST_ID);

    // fetch apstag bids, set bid targting, then call headerBidderBack
    // to get the ads for the first time
    window.apstag.fetchBids(
      {
        slots: apstagSlots,
        timeout: headerBiddingConf.BID_TIMEOUT
      },
      () => {
        headerBidderBack('apstag');
      }
    );

    const pbjsConfig = setPrebidConfig(headerBiddingConf);

    // request bids from prebid
    window.pbjs.que.push(() => {
      window.pbjs.setConfig(pbjsConfig);
      window.pbjs.addAdUnits(adUnits);
      window.pbjs.requestBids({
        bidsBackHandler: () => {
          headerBidderBack('prebid');
        }
      });
    });
  }

  await requestBids();

  // set timeout to send request to call sendAdserverRequest() after timeout
  // if all bidders haven't returned before then
  window.setTimeout(() => {
    sendAdserverRequest();
  }, headerBiddingConf.FAILSAFE_TIMEOUT);
}
